import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronRight, BarChart3, ShieldCheck, Zap, Smartphone, LayoutDashboard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './LukosInteractiveSolutions.css';

interface LukosInteractiveSolutionsProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

const LukosInteractiveSolutions: React.FC<LukosInteractiveSolutionsProps> = ({ activeTab, onTabChange }) => {
  const navigate = useNavigate();

  const content = {
    'LUK': {
      title: <>LUK: consulte e execute <br/><span>no ERP</span></>,
      subtitle: 'Peça consultas e tarefas do ERP em linguagem natural. A LUK responde dentro das permissões do usuário e pede confirmação antes de executar.',
      bg: '/assets/lukos_ai_premium_mockup_3d.png',
      color: '#7f00ff', // Purple
      route: '/lukos-ai',
    },
    'Conciliação': {
      title: <>Diga adeus às <br/><span>perdas financeiras</span></>,
      subtitle: 'Automação total da conferência de cartões, taxas e depósitos bancários. Tenha certeza de que cada centavo vendido caiu na sua conta.',
      bg: '/lukos_conciliacao_perfect_logo.png',
      color: '#10b981', // Green
      route: '/lukos-conciliacao',
    },
    'PDV e Frente de Caixa': {
      title: <>Agilidade que <br/><span>movimenta sua pista</span></>,
      subtitle: 'Alta performance do bico ao cupom fiscal. O PDV Lukos foi projetado para a dinâmica real de um posto: rápido, seguro e totalmente integrado.',
      bg: '/lukos_pdv_attendant_track.png',
      color: '#0ea5e9', // Blue
      route: '/lukos-pdv',
    },
    'Lukos Pay': {
      title: <>PDV Móvel e <br/><span>Pagamentos</span></>,
      subtitle: 'Transforme sua maquininha em um terminal de vendas completo na palma da mão. Realize operações de pista e receba pagamentos com conciliação automática.',
      bg: '/lukos_pay_authentic_operation.png',
      color: '#6366f1', // Indigo
      route: '/lukos-pay',
    },
    'Fidelidade': {
      title: <>Fidelize com <br/><span>Cashback real</span></>,
      subtitle: 'Aumente o retorno dos seus clientes com um programa de fidelidade moderno. Ofereça cashback automático e campanhas personalizadas direto no app.',
      bg: '/lukos_fidelidade_premium.png',
      color: '#ec4899', // Pink
      route: '/lukos-fidelidade',
    }
  };

  const tabs = Object.keys(content);
  const current = content[activeTab as keyof typeof content];

  const anchorMap: Record<string, string> = {
    'LUK': 'secao-luk',
    'Conciliação': 'secao-conciliacao',
    'PDV e Frente de Caixa': 'secao-pdv',
    'Lukos Pay': 'secao-pay',
    'Fidelidade': 'secao-fidelidade',
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    } else {
      // Seção não existe nesta página (ex: usado fora de /solucoes) — navega até lá
      navigate(`/solucoes#${id}`);
    }
  };

  const handleTabChange = (tab: string) => {
    onTabChange(tab);
    // Rola só até o topo desta seção (não da página) — importante quando o
    // componente é embutido no meio de outra página, como a Home.
    const el = document.getElementById('solucoes-interativas');
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const handleStartAction = () => scrollToSection(anchorMap[activeTab] ?? 'solucoes-completas');

  return (
    <section 
      className="lis-section" 
      id="solucoes-interativas" 
      style={{ 
        backgroundImage: `url(${current.bg})`,
        '--active-color': current.color,
      } as React.CSSProperties}
    >
      <div className="lis-overlay"></div>
      <div className="lis-container">
        
        {/* Navigation Tabs */}
        <nav className="lis-nav">
          <div className="lis-nav-pill">
            {tabs.map((tab) => (
              <div 
                key={tab}
                className={`lis-nav-item ${activeTab === tab ? 'active' : ''}`}
                onClick={() => handleTabChange(tab)}
                style={{ '--tab-color': content[tab as keyof typeof content].color } as React.CSSProperties}
              >
                {tab}
              </div>
            ))}
          </div>
        </nav>

        {/* Main Content Grid */}
        <div className="lis-content-wrapper">
          <div className={`lis-grid-container ${['Lukos Pay', 'Gestão de Preços'].includes(activeTab) ? 'has-mockup' : ''}`}>
            
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="lis-text-side"
            >
              <h2 className="lis-title">
                {current.title}
              </h2>
              <p className="lis-subtitle">
                {current.subtitle}
              </p>
              
              <div className="lis-actions">
                <button 
                  onClick={handleStartAction}
                  className="lis-cta-btn border-none cursor-pointer"
                >
                  Começar agora <ArrowRight size={18} />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LukosInteractiveSolutions;